import React, { useState, useMemo } from 'react'
import { Table, Button, Badge, Alert } from 'react-bootstrap'
import { Observacioneq } from '../Equipo/Observacioneq'
import { Modales } from '../Modales'
import { ViewImage } from './ViewImage'
import { computarGrupoInfoMap } from '../../utils/gruposEquipos'

/**
 * Listaequipos — pestaña 2 del flujo /ingresos/crear.
 *
 * Muestra la tabla de equipos seleccionados con estilo moderno (react-bootstrap Table),
 * columna de relación de grupos padre↔dependiente (spec items-adicionales-equipo-dependiente)
 * y botón para volver a la selección sin perder el estado.
 *
 * Fix vs versión anterior:
 *  - Marca/Modelo/Serie/Consecutivo se leen del snapshot (eq.snapshot.marca, no
 *    eq.equipo.marca) — el shape de Eqselect es EquipmentInOt completo.
 */
export const Listaequipos = ({ Eqselect, setPestaña }) => {
  const [equipoinfo, setEquipoinfo] = useState({})
  const [estadomodal, setEstadomodal] = useState(false)
  const [tarea, setTarea] = useState()

  const observacion = (eq) => {
    setEquipoinfo(eq)
    setTarea('obs')
    setEstadomodal(true)
  }
  const viewImg = (eq) => {
    setEquipoinfo(eq)
    setTarea('img')
    setEstadomodal(true)
  }

  // Grupos padre↔hijos entre los seleccionados (spec items-adicionales-equipo-dependiente).
  const grupoInfoByIdx = useMemo(() => {
    const info = computarGrupoInfoMap(Eqselect)
    const byIdx = new Map()
    ;(Eqselect || []).forEach((eq, idx) => {
      const g = info.get(String(eq._id))
      if (g) byIdx.set(idx, g)
    })
    return byIdx
  }, [Eqselect])

  return (
    <div>
      <Modales
        estado={estadomodal}
        cambiarestado={setEstadomodal}
        titulo={tarea === 'obs' ? 'Observación Ingreso' : 'Evidencias Fotográficas'}
      >
        {tarea === 'obs' && (
          <Observacioneq cambiarestado={setEstadomodal} infoobservacion={equipoinfo?.observacion} />
        )}
        {tarea === 'img' && (
          <ViewImage cambiarestado={setEstadomodal} eqinfo={equipoinfo} />
        )}
      </Modales>

      <div className="d-flex align-items-center gap-2 mb-2">
        <Badge bg="primary">
          {Eqselect?.length || 0} equipo(s) seleccionado(s)
        </Badge>
        {typeof setPestaña === 'function' && (
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setPestaña(1)}
            title="Volver al listado sin perder la selección actual"
          >
            <i className="bi bi-arrow-left me-1" />
            Volver a selección
          </Button>
        )}
      </div>

      {(!Eqselect || Eqselect.length === 0) ? (
        <Alert variant="info" className="text-center">
          <i className="bi bi-inbox fs-3 d-block mb-2" />
          Aún no has seleccionado equipos. Vuelve al listado y marca al menos uno.
        </Alert>
      ) : (
        <Table hover striped responsive size="sm">
          <thead className="table-dark">
            <tr>
              <th style={{ width: 42 }} className="text-center" title="Relación padre/dependiente">
                <i className="bi bi-diagram-3" />
              </th>
              <th>Ítem</th>
              <th>Marca</th>
              <th>Modelo</th>
              <th>Serie</th>
              <th>Consec.</th>
              <th>Estado</th>
              <th className="text-center">Accesorios</th>
              <th>¿Cuáles?</th>
              <th className="text-center">Obs.</th>
              <th className="text-center">Fotos</th>
            </tr>
          </thead>
          <tbody>
            {Eqselect.map((eq, idx) => {
              const g = grupoInfoByIdx.get(idx)
              const tieneAccesorios =
                eq?.accesorios && eq.accesorios.length > 0 && eq.accesorios !== 'NINGUNO'
              return (
                <tr key={eq._id}>
                  {/* Pareja */}
                  <td className="text-center align-middle" style={{ padding: '2px 1px' }}>
                    {g ? (
                      <span
                        className={g.rol === 'padre' ? 'bg-primary text-white' : 'bg-info text-dark'}
                        style={{
                          display: 'inline-block',
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          padding: '2px 4px',
                          lineHeight: 1.1,
                          whiteSpace: 'nowrap',
                          letterSpacing: '-0.03em',
                          borderRadius: '3px',
                        }}
                        title={
                          g.rol === 'padre'
                            ? `Padre del grupo ${g.grupoNum}`
                            : `Dependiente ${g.grupoNum}${g.letra || ''} del grupo ${g.grupoNum}`
                        }
                      >
                        {g.rol === 'padre' ? `P${g.grupoNum}` : `D${g.grupoNum}${g.letra || ''}`}
                      </span>
                    ) : (
                      <span className="text-muted" style={{ fontSize: '0.7rem' }}>—</span>
                    )}
                  </td>

                  <td>{eq?.snapshot?.item}</td>
                  <td>{eq?.snapshot?.marca}</td>
                  <td>{eq?.snapshot?.modelo}</td>
                  <td>{eq?.snapshot?.serial}</td>
                  <td className="text-center">
                    <Badge bg="secondary">{eq?.consecutivo || 'N/A'}</Badge>
                  </td>
                  <td>
                    <Badge bg="warning" text="dark">{eq?.estado}</Badge>
                  </td>
                  <td className="text-center">
                    {tieneAccesorios ? (
                      <Badge bg="success">Sí</Badge>
                    ) : (
                      <Badge bg="light" text="dark">No</Badge>
                    )}
                  </td>
                  <td className="small">
                    {tieneAccesorios ? eq.accesorios : <span className="text-muted">NINGUNO</span>}
                  </td>
                  <td className="text-center">
                    {eq?.observacion ? (
                      <i
                        className="bi bi-eye-fill text-primary"
                        onClick={() => observacion(eq)}
                        style={{ cursor: 'pointer' }}
                        title="Ver observación"
                      />
                    ) : (
                      <span className="text-muted small">—</span>
                    )}
                  </td>
                  <td className="text-center">
                    {eq?.fotos?.length > 0 ? (
                      <span
                        onClick={() => viewImg(eq)}
                        style={{ cursor: 'pointer' }}
                        title={`Ver ${eq.fotos.length} foto(s)`}
                      >
                        <i className="bi bi-images text-primary me-1" />
                        <Badge bg="info" style={{ fontSize: '0.65rem' }}>{eq.fotos.length}</Badge>
                      </span>
                    ) : (
                      <span className="text-muted small">—</span>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </Table>
      )}
    </div>
  )
}
